import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { FAQ_ITEMS, TIRAMISUS } from "../data/content";
import { Instagram, Send, Mail, AtSign } from "lucide-react";
import { CloudDivider } from "./CloudDivider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mergeProducts = (apiProducts) =>
  TIRAMISUS.map((staticP) => {
    const dynP = apiProducts.find((x) => x.id === staticP.id);
    return dynP ? { ...staticP, price: dynP.price, in_stock: dynP.in_stock, name: dynP.name } : staticP;
  });

export const FaqContact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [order, setOrder] = useState({
    customer_name: "",
    email: "",
    phone: "",
    notes: "",
    items: {},
  });
  const [loading, setLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [allProducts, setAllProducts] = useState(TIRAMISUS);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setAllProducts(mergeProducts(r.data.products || [])))
      .catch(() => {});
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé ! On te répond très vite.");
      setForm({ name: "", email: "", message: "" });
    } catch (e) {
      toast.error("Erreur d'envoi. Réessaie dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  const updateQty = (id, delta) => {
    setOrder((o) => {
      const items = { ...o.items };
      const next = (items[id] || 0) + delta;
      if (next <= 0) delete items[id];
      else items[id] = next;
      return { ...o, items };
    });
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    const items = Object.entries(order.items);
    if (items.length === 0) {
      toast.error("Ajoute au moins un produit à ta commande.");
      return;
    }
    setOrderLoading(true);
    try {
      const payload = {
        customer_name: order.customer_name,
        email: order.email,
        phone: order.phone,
        notes: order.notes,
        items: items.map(([id, qty]) => {
          const p = allProducts.find((x) => x.id === id);
          return { product_id: id, name: p?.name || id, quantity: qty };
        }),
      };
      const r = await axios.post(`${API}/orders`, payload);
      toast.success(r.data.message || "Commande envoyée !");
      setOrder({ customer_name: "", email: "", phone: "", notes: "", items: {} });
    } catch (e) {
      toast.error("Erreur. Vérifie tes infos et réessaie.");
    } finally {
      setOrderLoading(false);
    }
  };

  const total = Object.entries(order.items).reduce((sum, [id, qty]) => {
    const p = allProducts.find((x) => x.id === id);
    return sum + (p?.price || 0) * qty;
  }, 0);
  const totalQty = Object.values(order.items).reduce((s, q) => s + q, 0);

  return (
    <>
      {/* COMMANDER */}
      <section
        id="commander"
        data-testid="order-section"
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,209,220,0.85) 0%, rgba(255,230,236,0.85) 100%)",
        }}
      >
        <CloudDivider position="top" color="rgba(251,247,242,0.8)" />
        <CloudDivider position="bottom" color="rgba(251,247,242,0.8)" flip />
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs uppercase tracking-[0.2em] text-coral font-medium">
              Click & Collect
            </span>
            <h2 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02]">
              Commande tes
              <span className="italic text-coral"> gourmandises.</span>
            </h2>
            <p className="mt-4 text-amyu-muted">
              Remplis le formulaire — on te confirme par email sous 24h avec le point de retrait.
            </p>
          </div>

          <form
            onSubmit={submitOrder}
            data-testid="order-form"
            className="grid lg:grid-cols-5 gap-6 bg-white rounded-[32px] p-5 md:p-10 shadow-[0_20px_60px_rgba(229,91,91,0.1)] overflow-hidden"
          >
            <div className="lg:col-span-3 min-w-0">
              <h3 className="font-display text-2xl mb-4">Ma sélection</h3>
              <div className="space-y-2 max-h-[420px] overflow-y-auto -mr-2 pr-2">
                {allProducts.map((p) => {
                  const qty = order.items[p.id] || 0;
                  const outOfStock = p.in_stock === false;
                  return (
                    <div
                      key={p.id}
                      data-testid={`order-product-${p.id}`}
                      className={`flex items-center gap-2.5 p-2.5 sm:p-3 rounded-2xl transition-colors ${
                        outOfStock ? "opacity-50" : "hover:bg-amyu-cream"
                      }`}
                    >
                      <img src={p.image} alt={p.name} className={`shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover ${outOfStock ? "grayscale" : ""}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm sm:text-base text-amyu-ink truncate">{p.name}</p>
                        <p className="text-[11px] sm:text-xs text-coral font-medium sm:hidden">
                          {p.price.toFixed(2).replace(".", ",")} €
                        </p>
                        <p className="hidden sm:block text-xs text-amyu-muted truncate">
                          {outOfStock ? "Bientôt de retour" : p.tagline}
                        </p>
                      </div>
                      <span
                        data-testid={`order-price-${p.id}`}
                        className="hidden sm:inline text-sm font-medium text-coral whitespace-nowrap"
                      >
                        {p.price.toFixed(2).replace(".", ",")} €
                      </span>
                      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => updateQty(p.id, -1)}
                          disabled={outOfStock}
                          data-testid={`order-minus-${p.id}`}
                          className="w-8 h-8 rounded-full bg-amyu-cream hover:bg-coral hover:text-white transition-colors disabled:opacity-30 text-base leading-none"
                        >
                          −
                        </button>
                        <span className="w-5 sm:w-6 text-center font-medium text-sm">{qty}</span>
                        <button
                          type="button"
                          onClick={() => updateQty(p.id, 1)}
                          disabled={outOfStock}
                          data-testid={`order-plus-${p.id}`}
                          className="w-8 h-8 rounded-full bg-coral text-white hover:bg-[#c94545] transition-colors disabled:opacity-30 text-base leading-none"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-2 flex flex-col gap-3 min-w-0">
              <input
                required
                value={order.customer_name}
                onChange={(e) => setOrder({ ...order, customer_name: e.target.value })}
                placeholder="Prénom & nom"
                data-testid="order-name"
                className="w-full min-w-0 bg-amyu-cream rounded-full px-5 py-3.5 outline-none focus:ring-2 focus:ring-coral/30"
              />
              <input
                required
                type="email"
                value={order.email}
                onChange={(e) => setOrder({ ...order, email: e.target.value })}
                placeholder="Email"
                data-testid="order-email"
                className="w-full min-w-0 bg-amyu-cream rounded-full px-5 py-3.5 outline-none focus:ring-2 focus:ring-coral/30"
              />
              <input
                value={order.phone}
                onChange={(e) => setOrder({ ...order, phone: e.target.value })}
                placeholder="Téléphone (optionnel)"
                data-testid="order-phone"
                className="w-full min-w-0 bg-amyu-cream rounded-full px-5 py-3.5 outline-none focus:ring-2 focus:ring-coral/30"
              />
              <textarea
                value={order.notes}
                onChange={(e) => setOrder({ ...order, notes: e.target.value })}
                rows={4}
                placeholder="Notes ou allergies ?"
                data-testid="order-notes"
                className="w-full min-w-0 bg-amyu-cream rounded-3xl px-5 py-3.5 outline-none focus:ring-2 focus:ring-coral/30 resize-none"
              />
              <div
                data-testid="order-total"
                className="bg-amyu-cream rounded-3xl px-5 py-4 mt-1"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.18em] text-amyu-muted font-medium">
                    Total
                  </span>
                  <span className="font-display text-3xl text-coral">
                    {total.toFixed(2).replace(".", ",")} €
                  </span>
                </div>
                <div className="text-xs text-amyu-muted mt-1">
                  {totalQty} produit{totalQty > 1 ? "s" : ""} sélectionné{totalQty > 1 ? "s" : ""}
                </div>
              </div>
              <button
                type="submit"
                disabled={orderLoading}
                data-testid="order-submit"
                className="btn-coral inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Send size={16} />
                {orderLoading ? "Envoi..." : "Envoyer ma commande"}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* FAQ + CONTACT */}
      <section
        id="contact"
        data-testid="contact-section"
        className="relative py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
          {/* FAQ */}
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-coral font-medium">FAQ</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
              Les réponses à
              <span className="italic text-coral"> tes questions.</span>
            </h2>
            <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-amyu/30"
                >
                  <AccordionTrigger className="text-left font-display text-xl py-5 hover:text-coral">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-amyu-muted leading-relaxed pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Contact form + socials */}
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-coral font-medium">Contact</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
              Écris-nous
              <span className="font-hand text-coral text-5xl"> un mot.</span>
            </h2>

            <form
              onSubmit={submitContact}
              data-testid="contact-form"
              className="mt-8 bg-white rounded-[28px] p-5 md:p-7 shadow-[0_12px_40px_rgba(42,36,34,0.06)] flex flex-col gap-3 overflow-hidden"
            >
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ton prénom"
                data-testid="contact-name"
                className="w-full min-w-0 bg-amyu-cream rounded-full px-5 py-3.5 outline-none focus:ring-2 focus:ring-coral/30"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Ton email"
                data-testid="contact-email"
                className="w-full min-w-0 bg-amyu-cream rounded-full px-5 py-3.5 outline-none focus:ring-2 focus:ring-coral/30"
              />
              <textarea
                required
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                placeholder="Ton message..."
                data-testid="contact-message"
                className="w-full min-w-0 bg-amyu-cream rounded-3xl px-5 py-3.5 outline-none focus:ring-2 focus:ring-coral/30 resize-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-coral inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Send size={16} />
                {loading ? "Envoi..." : "Envoyer mon message"}
              </button>
            </form>

            <div className="mt-8 flex flex-wrap gap-3" data-testid="social-links">
              <a
                href="https://instagram.com/amyu.off"
                target="_blank"
                rel="noreferrer"
                data-testid="social-instagram"
                className="inline-flex items-center gap-2 bg-white pill px-5 py-3 border border-amyu hover:border-coral transition-colors"
              >
                <Instagram size={16} className="text-coral" /> @amyu.off
              </a>
              <a
                href="https://tiktok.com/@amyu.off"
                target="_blank"
                rel="noreferrer"
                data-testid="social-tiktok"
                className="inline-flex items-center gap-2 bg-white pill px-5 py-3 border border-amyu hover:border-coral transition-colors"
              >
                <AtSign size={16} className="text-coral" /> TikTok
              </a>
              <a
                href="https://snapchat.com/add/amyu.off"
                target="_blank"
                rel="noreferrer"
                data-testid="social-snapchat"
                className="inline-flex items-center gap-2 bg-white pill px-5 py-3 border border-amyu hover:border-coral transition-colors"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-coral"
                  aria-hidden="true"
                >
                  <path d="M12 2.5c3 0 5.4 2.1 5.4 5.2 0 1.2-.1 2.4-.2 3.1.4.2.9.3 1.4.3.4 0 .9-.1 1.2-.2.1 0 .3-.1.4-.1.3 0 .6.2.7.5.1.4-.2.7-.5.8-.1 0-.7.2-1.5.5-.3.1-.5.3-.5.7 0 .3.7 1.6 3 2.4.2.1.4.3.4.5 0 .6-1.6 1.2-3.1 1.4-.1.2-.2.7-.3 1-.1.2-.2.3-.5.3-.3 0-.7-.1-1.3-.2-.4-.1-.8-.1-1.2-.1-.5 0-1 .1-1.4.4-.9.6-1.9 1.5-3.5 1.5s-2.6-.9-3.5-1.5c-.4-.3-.9-.4-1.4-.4-.4 0-.8 0-1.2.1-.5.1-1 .2-1.3.2-.3 0-.4-.1-.5-.3-.1-.3-.2-.8-.3-1-1.5-.2-3.1-.8-3.1-1.4 0-.2.2-.4.4-.5 2.3-.8 3-2.1 3-2.4 0-.4-.2-.6-.5-.7-.8-.3-1.4-.5-1.5-.5-.3-.1-.6-.4-.5-.8.1-.3.4-.5.7-.5.1 0 .3.1.4.1.3.1.8.2 1.2.2.5 0 1-.1 1.4-.3-.1-.7-.2-1.9-.2-3.1 0-3.1 2.4-5.2 5.4-5.2z" />
                </svg>{" "}
                @amyu.off
              </a>
              <a
                href="mailto:amyu.contact@gmail.com"
                data-testid="social-email"
                className="inline-flex items-center gap-2 bg-white pill px-5 py-3 border border-amyu hover:border-coral transition-colors"
              >
                <Mail size={16} className="text-coral" /> amyu.contact@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
